import { lazy, Suspense, useCallback, useMemo } from 'react';
import { useRestaurantSettings } from '../../../hooks/useRestaurantSettings';
import { PIN_TYPES, type MarkerData, type PinType } from './pins';
import './MarkersWidget.scss';

const MarkersMapEditor = lazy(() => import('./MarkersMapEditor'));

const isBrowser = typeof window !== 'undefined';

const MarkersWidget = ({ id, value, onChange, title, description }: any) => {
  const settings = useRestaurantSettings();
  // Memoised so the `|| []` fallback does not produce a new array on every
  // render, which would change the identity of the useCallback deps below.
  const markers: MarkerData[] = useMemo(() => value || [], [value]);

  const update = useCallback(
    (next: MarkerData[]) => onChange(id, next),
    [id, onChange],
  );

  const handleDrag = useCallback(
    (index: number, lat: number, lng: number) => {
      const next = [...markers];
      next[index] = { ...next[index], lat, lng };
      update(next);
    },
    [markers, update],
  );

  const handleAdd = useCallback(
    (lat: number, lng: number) => {
      update([...markers, { type: 'restaurant', label: '', lat, lng }]);
    },
    [markers, update],
  );

  const handleRemove = useCallback(
    (index: number) => {
      update(markers.filter((_, i) => i !== index));
    },
    [markers, update],
  );

  const handleField = useCallback(
    (index: number, field: string, val: string) => {
      const next = [...markers];
      next[index] = { ...next[index], [field]: val };
      update(next);
    },
    [markers, update],
  );

  const defaultCenter = {
    lat: settings.latitude || 50.5,
    lng: settings.longitude || 4.5,
  };

  return (
    <div className="markers-widget">
      <h4 className="markers-widget-title">{title}</h4>
      {description && <p className="markers-widget-help">{description}</p>}

      {isBrowser && (
        <Suspense
          fallback={<div className="markers-widget-loading">Chargement…</div>}
        >
          <MarkersMapEditor
            markers={markers}
            defaultCenter={defaultCenter}
            onDrag={handleDrag}
            onAdd={handleAdd}
            apiKey={settings.carto_api_key}
          />
        </Suspense>
      )}

      {markers.length > 0 && (
        <div className="markers-list">
          {markers.map((m, i) => (
            <div key={i} className="marker-item">
              <div className="marker-item-header">
                <span className="marker-item-index">{i + 1}</span>
                <button
                  type="button"
                  className="marker-item-remove"
                  title="Supprimer"
                  onClick={() => handleRemove(i)}
                >
                  ×
                </button>
              </div>
              <label className="marker-field">
                <span>Type</span>
                <select
                  value={m.type || 'restaurant'}
                  onChange={(e) =>
                    handleField(i, 'type', e.target.value as PinType)
                  }
                >
                  {PIN_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="marker-field">
                <span>Libellé</span>
                <input
                  type="text"
                  value={m.label || ''}
                  placeholder="Texte au clic…"
                  onChange={(e) => handleField(i, 'label', e.target.value)}
                />
              </label>
              <div className="marker-coords">
                {m.lat?.toFixed(5)}, {m.lng?.toFixed(5)}
              </div>
            </div>
          ))}
        </div>
      )}

      {markers.length === 0 && (
        <p className="markers-widget-empty">
          Aucun marqueur. Centrez la carte et cliquez « Ajouter ici ».
        </p>
      )}
    </div>
  );
};

export default MarkersWidget;
